/**
 * DebugVisualization - Reusable debug visualization utilities
 *
 * Provides helper functions to create debug visualizations for rays, points,
 * and other geometric primitives in the scene.
 */

import * as THREE from 'three';

/** Minimal interface for Scene class */
export interface IPotreeScene {
  scene: THREE.Scene;
}

/** Minimal interface for viewer with scene */
export interface IViewerScene {
  scene: IPotreeScene;
}

export interface DebugGroupOptions {
  /** Whether the group should be visible by default */
  visible?: boolean;
  /**
   * World-space origin for the group. Geometry inside the group is stored
   * relative to this origin so float32 precision stays usable when scenes
   * are far from world (0,0,0). The group's `position` is set to this value.
   */
  origin?: THREE.Vector3;
}

export interface RayDebugOptions {
  /** Color for rays that hit (default: green) */
  hitColor?: number;
  /** Color for rays that miss (default: red) */
  missColor?: number;
  /** Opacity for ray lines (default: 0.3) */
  opacity?: number;
}

export interface PointDebugOptions {
  /** Color for points (default: yellow) */
  color?: number;
  /** Size of point spheres (default: 0.02) */
  size?: number;
  /** Opacity for points (default: 0.6) */
  opacity?: number;
}

/** Stored ray data for lazy visualization */
export interface StoredRayData {
  origin: THREE.Vector3;
  direction: THREE.Vector3;
  distance: number;
  hasHit: boolean;
  options?: RayDebugOptions;
}

/** Stored point data for lazy visualization */
export interface StoredPointData {
  position: THREE.Vector3;
  options?: PointDebugOptions;
}

/** Extended THREE.Group with stored data for lazy rendering */
interface LazyDebugGroup extends THREE.Group {
  __debugData?: {
    rays: StoredRayData[];
    points: StoredPointData[];
  };
}

/**
 * Create or recreate a debug group in the scene
 * Removes any existing group with the same name and creates a new one
 * Preserves visibility state if an existing group was visible
 */
export function createDebugGroup(viewer: IViewerScene, name: string, options: DebugGroupOptions = {}): THREE.Group {
  let { visible = false } = options;

  // Remove existing group if it exists, but preserve visibility state
  const existing = viewer.scene.scene.children.find((child: THREE.Object3D) => child.name === name) as LazyDebugGroup | undefined;
  if (existing) {
    // Preserve visibility state from existing group
    visible = existing.visible;
    viewer.scene.scene.remove(existing);
    if (existing.traverse) {
      existing.traverse((child: THREE.Object3D) => {
        const mesh = child as THREE.Mesh;
        if (mesh.geometry) mesh.geometry.dispose();
        if (mesh.material) {
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((m) => m.dispose());
          } else {
            mesh.material.dispose();
          }
        }
      });
    }
  }

  // Create new group with data storage
  const group = new THREE.Group() as LazyDebugGroup;
  group.name = name;
  group.visible = visible;
  if (options.origin) {
    group.position.copy(options.origin);
  }
  group.__debugData = {
    rays: [],
    points: [],
  };
  viewer.scene.scene.add(group);

  return group;
}

/**
 * Add a ray to a debug group (stores data, creates visualization only when visible)
 */
export function addRayToDebugGroup(group: THREE.Group, origin: THREE.Vector3, direction: THREE.Vector3, distance: number, hasHit: boolean, options: RayDebugOptions = {}): void {
  const lazyGroup = group as LazyDebugGroup;

  // Store ray data
  if (lazyGroup.__debugData) {
    lazyGroup.__debugData.rays.push({
      origin,
      direction,
      distance,
      hasHit,
      options,
    });
  }

  // Only create visualization if group is visible
  if (group.visible) {
    createRayVisualization(group, origin, direction, distance, hasHit, options);
  }
}

/**
 * Internal: Create the actual THREE.js ray visualization
 */
function createRayVisualization(group: THREE.Group, origin: THREE.Vector3, direction: THREE.Vector3, distance: number, hasHit: boolean, options: RayDebugOptions = {}): void {
  const { hitColor = 0x00ff00, missColor = 0xff0000, opacity = 0.3 } = options;

  // Stored ray data is in world coordinates; geometry must be local to the group
  // so float32 precision holds when the scene is far from world (0,0,0).
  const localOrigin = origin.clone().sub(group.position);
  const localEnd = localOrigin.clone().add(direction.clone().multiplyScalar(distance));
  const color = hasHit ? hitColor : missColor;

  const geometry = new THREE.BufferGeometry().setFromPoints([localOrigin, localEnd]);
  const material = new THREE.LineBasicMaterial({ color, opacity, transparent: true });
  const line = new THREE.Line(geometry, material);
  group.add(line);
}

/**
 * Add a point to a debug group (stores data, creates visualization only when visible)
 */
export function addPointToDebugGroup(group: THREE.Group, position: THREE.Vector3, options: PointDebugOptions = {}): void {
  const lazyGroup = group as LazyDebugGroup;

  // Store point data
  if (lazyGroup.__debugData) {
    lazyGroup.__debugData.points.push({
      position,
      options,
    });
  }

  // Only create visualization if group is visible
  if (group.visible) {
    createPointVisualization(group, position, options);
  }
}

/**
 * Internal: Create the actual THREE.js point visualization
 */
function createPointVisualization(group: THREE.Group, position: THREE.Vector3, options: PointDebugOptions = {}): void {
  const { color = 0xffff00, size = 0.02, opacity = 0.6 } = options;

  const sphereGeometry = new THREE.SphereGeometry(size, 8, 8);
  const sphereMaterial = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
  });
  const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
  // Local to the group so float32 stays usable at far-from-origin scenes.
  sphere.position.copy(position).sub(group.position);
  group.add(sphere);
}

/**
 * Add multiple points to a debug group
 */
export function addPointsToDebugGroup(group: THREE.Group, points: THREE.Vector3[], options: PointDebugOptions = {}): void {
  for (const point of points) {
    addPointToDebugGroup(group, point, options);
  }
}

/**
 * Toggle visibility of debug groups by name pattern
 * Rebuilds visualizations from stored data when making visible
 */
export function toggleDebugGroupVisibility(viewer: IViewerScene, namePattern: string, visible: boolean): void {
  for (const child of viewer.scene.scene.children) {
    if (child.name && child.name.includes(namePattern)) {
      const group = child as LazyDebugGroup;

      // If making visible, rebuild visualizations from stored data
      if (visible && group.__debugData) {
        // Clear existing visualizations
        while (group.children.length > 0) {
          const obj = group.children[0];
          group.remove(obj);
          const mesh = obj as THREE.Mesh;
          if (mesh.geometry) mesh.geometry.dispose();
          if (mesh.material) {
            if (Array.isArray(mesh.material)) {
              mesh.material.forEach((m) => m.dispose());
            } else {
              mesh.material.dispose();
            }
          }
        }

        // Recreate all stored rays
        for (const rayData of group.__debugData.rays) {
          createRayVisualization(group, rayData.origin, rayData.direction, rayData.distance, rayData.hasHit, rayData.options);
        }

        // Recreate all stored points
        for (const pointData of group.__debugData.points) {
          createPointVisualization(group, pointData.position, pointData.options);
        }
      }

      group.visible = visible;
    }
  }
}
